import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/components/ConfirmDialog";
import Logo from "@/components/Logo";
import BackButton from "@/components/BackButton";
import LanguageToggle from "@/components/LanguageToggle";
import { ShieldCheck, SignOut, ClipboardText } from "@phosphor-icons/react";
import { changeRequestsApi } from "@/api/changeRequests";
import { fmtNum } from "@/utils/toEnglishDigits";

// Minimal sticky header: Logo (right in RTL) + user name + role badge + logout.
// Navigation moved to <BottomTabBar />.
export default function AppHeader() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === "en";

  const isAdmin = !!(user?.is_admin || user?.role === "admin");
  const [pendingCount, setPendingCount] = useState(0);

  // Poll pending change-requests count for admins (every 30s + on focus).
  useEffect(() => {
    if (!isAdmin) { setPendingCount(0); return; }
    let cancelled = false;
    const tick = async () => {
      try {
        const data = await changeRequestsApi.pendingCount();
        if (!cancelled) setPendingCount(Number(data?.count || 0));
      } catch { /* silent — header must never crash */ }
    };
    tick();
    const interval = setInterval(tick, 30_000);
    const onFocus = () => tick();
    window.addEventListener("focus", onFocus);
    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
    };
  }, [isAdmin]);

  const doLogout = async () => {
    const ok = await confirm({
      title: t("header.logoutTitle"),
      message: t("header.logoutMessage"),
      confirmText: t("header.logoutConfirm"),
      cancelText: t("header.logoutCancel"),
      tone: "danger",
    });
    if (!ok) return;
    logout();
    navigate("/login");
  };

  return (
    <header
      dir={isEn ? "ltr" : "rtl"}
      className="border-b border-primary/15 bg-[#0B1A3A]/95 backdrop-blur sticky top-0 z-30"
      data-testid="app-header"
    >
      <div className="max-w-full mx-auto px-4">
        <div className="flex items-center justify-between py-3 gap-3">
          <div className="flex items-center gap-3 flex-shrink-0">
            <button
              type="button"
              onClick={() => navigate("/")}
              className="flex items-center gap-3"
              data-testid="header-brand"
            >
              <Logo variant="brand" size={40} />
            </button>
            <BackButton />
          </div>

          <div className="flex items-center gap-3 text-sm flex-shrink-0">
            {user?.name && (
              <span className="text-white/80 hidden sm:inline" data-testid="header-user">
                {user.name}
              </span>
            )}
            {isAdmin && (
              <button
                type="button"
                onClick={() => navigate("/admin/change-requests")}
                data-testid="header-cr-badge"
                title={t("header.pendingRequests")}
                className="relative inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-primary/40 bg-primary/10 text-primary hover:bg-primary/20 text-xs font-bold transition-all"
              >
                <ClipboardText size={13} weight="duotone" />
                <span className="hidden sm:inline">{t("header.requests")}</span>
                {pendingCount > 0 && (
                  <span
                    className="absolute -top-1.5 -left-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-white text-[10px] font-black flex items-center justify-center num shadow-[0_2px_6px_rgba(239,68,68,0.6)]"
                    data-testid="header-cr-count"
                    dir="ltr"
                  >
                    {pendingCount > 99 ? "99+" : fmtNum(pendingCount)}
                  </span>
                )}
              </button>
            )}
            {isAdmin && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-primary/40 bg-primary/10 text-primary text-xs">
                <ShieldCheck size={12} weight="fill" /> {t("header.adminBadge")}
              </span>
            )}
            <LanguageToggle compact />
            <button
              onClick={doLogout}
              data-testid="logout-btn"
              className="inline-flex items-center gap-1.5 text-destructive hover:text-white hover:bg-destructive/20 px-3 py-1.5 rounded-full border border-destructive/40 transition-all"
            >
              <SignOut size={14} weight="bold" />
              <span className="hidden md:inline">{t("header.logout")}</span>
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}
